<template>
  <span>
    <div class="d-flex align-items-center mb-3">
      <div class="title mr-3">
        <span v-if="form" class="text-muted"> #{{ form.no }} </span>
        {{ $t("task.edit") }}
      </div>
    </div>

    <div class="task-flex">
      <div>
        <div v-if="form._id && task">
          <button class="link-button mb-3" v-b-toggle.collapse-file-rocket><i class="iconoir-attachment"></i> {{ $t("file-manager.attach") }}</button>
          <b-collapse id="collapse-file-rocket" class="mt-2">
            <b-tabs class="nav-tabs-custom">
              <b-tab>
                <template v-slot:title>
                  <h6 class="m-1">{{ $t("file-manager.select-from-list") }}</h6>
                </template>
                <div class="ml-2 mt-4 mb-4">
                  <filter-select
                    class="w-75 d-inline-block"
                    id="file-filter"
                    ref="no-filter"
                    :placeholder="$t('file-manager.select-from-list')"
                    :multiple="false"
                    @filter="onFileSearch"
                    :selected="selectedFile"
                    @changed="selectFile"
                  />

                  <sweet-button @click="attachFile()" :disabled="!this.selectedFile" type="submit" variant="success" icon="mdi mdi-check" size="sm" buttonClass="ml-3">
                    {{ $t("common.save") }}
                  </sweet-button>
                </div>
              </b-tab>
              <b-tab>
                <template v-slot:title>
                  <h6 class="m-1">{{ $t("file-manager.upload-from-disk") }}</h6>
                </template>

                <div class="ml-2 mt-4 mb-4">
                  <file-rocket
                    :multiple="true"
                    :allowedFormats="allowedFormats"
                    @response="handleUploadResponse"
                    :showProgress="'Upload'"
                    :maxFileSize="600000000000"
                    :maxFileLength="1"
                    :targetUrl="targetUrl"
                    :payload="selectedFileIdInObject"
                  >
                  </file-rocket>
                </div>
              </b-tab>
            </b-tabs>
          </b-collapse>
        </div>

        <div>
          <sweet-input
            class="mt-3"
            v-model="form.name"
            :isValid="!$v.form.name.$error"
            @input="$v.form.name.$touch()"
            layout="vertical"
            :placeholder="$t('common.name')"
            :label="$t('task.name')"
          />

          <sweet-textarea
            class="mt-3"
            v-model="form.description"
            :isValid="!$v.form.description.$error"
            @input="$v.form.description.$touch()"
            type="text"
            layout="vertical"
            :placeholder="$t('task.description')"
            :label="$t('task.description')"
            :rows="10"
          />
        </div>

        <div v-if="form.status == 'DONE'">
          <label v-if="$v && $v.form.$anyError" for="doneReason" class="text-danger mt-1"> * {{ $t("task.required-field-warning") }} </label>

          <sweet-textarea
            v-model="form.doneReason"
            :isValid="!$v.form.doneReason.$error"
            @input="$v.form.doneReason.$touch()"
            type="text"
            layout="vertical"
            :placeholder="$t('task.done-reason')"
            :label="$t('task.done-reason')"
            :rows="3"
          />
        </div>
      </div>

      <div>
        <div class="title mb-3">
          {{ $t("common.detail") }}
        </div>

        <b-row class="align-items-center mb-3">
          <b-col class="bold-text text-muted">{{ $t("task.status.text") }} : </b-col>
          <b-col>
            <task-status-select :selected="form.status" @onSelect="onStatusSelect" />
          </b-col>
        </b-row>

        <b-row class="align-items-center mb-3">
          <b-col class="bold-text text-muted">{{ $t("organization.text") }} : </b-col>
          <b-col>
            <letter-avatar
              :summarize="true"
              :name="task ? task.organization.name : currentOrganization.name"
              :showName="true"
              size="xxs"
              :image="currentOrganization.logo"
              variant="secondary"
            />
          </b-col>
        </b-row>

        <div class="bold-text text-muted mb-1">{{ $t("task.assignee") }} :</div>
        <div class="mb-3">
          <organization-member-select :selected="form.assignee" @onSelect="onMemberSelect" :organization="task ? task.organization : currentOrganization" />
        </div>

        <div class="bold-text text-muted mb-1">{{ $t("task.priority.text") }} :</div>
        <div class="mb-3">
          <task-priority-select :selected="form.priority" @onSelect="onPrioritySelect" />
        </div>

        <div class="bold-text text-muted mb-1">{{ $t("task.category") }} :</div>
        <div class="mb-3">
          <filter-select
            id="categoryFilter"
            ref="categoryFilter"
            :placeholder="$t('task.category')"
            :multiple="false"
            @filter="onCategorySearch"
            :selected="form.category"
            @changed="onCategorySelect"
          />
        </div>

        <b-row class="align-items-center mb-3">
          <b-col class="bold-text text-muted ">{{ $t("task.duedate") }} : </b-col>
          <b-col>
            <date-picker
              v-model="form.duedate"
              :disabled-date="(date) => date < new Date()"
              :first-day-of-week="1"
              :lang="language"
              format="DD.MM.YYYY"
              value-type="timestamp"
              :placeholder="$t('task.duedate')"
            />
          </b-col>
        </b-row>

        <b-row v-if="form.status == 'DONE'" class="align-items-center mb-3">
          <b-col class="bold-text text-muted ">{{ $t("task.donedate") }} : </b-col>
          <b-col>
            <date-picker
              v-model="form.donedate"
              :disabled-date="(date) => date < new Date()"
              :first-day-of-week="1"
              :lang="language"
              format="DD.MM.YYYY"
              value-type="timestamp"
              :placeholder="$t('task.donedate')"
            />
          </b-col>
        </b-row>
      </div>
    </div>

    <div class="pb-3">
      <div v-if="form.taskRelations && form.taskRelations.length > 0">
        <h4 class="mt-4 mb-3"><i class="iconoir-link"></i> {{ $t("task.link.title") }}</h4>

        <div v-for="i in form.taskRelations" :key="'lt' + i.task._id + form._id + i.type">
          <task-item :task="i.task" :linkType="i.type" :unlink="true" :relatedTask="form" @unlinked="unlink(i)" />
        </div>
      </div>

      <h6 class="mt-4">{{ $t("task.link.do") }}</h6>
      <b-row>
        <b-col md="7">
          <task-link-select @onSelect="selectLinkTask" :selected="taskRelation" />
        </b-col>

        <b-col>
          <select v-model="linkType" class="form-control">
            <option value="BLOCKS">{{ $t("task.link.blocks") }}</option>
            <option value="BLOCKED_BY">{{ $t("task.link.blocked-by") }}</option>
            <option value="RELATED">{{ $t("task.link.related") }}</option>
            <option value="SUBTASK">{{ $t("task.link.sub-task") }}</option>
            <option value="PARENT">{{ $t("task.link.parent") }}</option>
          </select>
        </b-col>

        <b-col md="2">
          <button class="btn  btn-secondary" @click="link" :disabled="!linkButtonEnable"><i class="bx bx-link"></i> {{ $t("task.link.make") }}</button>
        </b-col>
      </b-row>
    </div>
  </span>
</template>

<script>
import FileRocket from "@c/form/file-rocket";
import SweetTextarea from "@c/form/sweet-textarea";
import OrganizationMemberSelect from "@cc/organization-member-select";
import TaskLinkSelect from "@cc/task/task-link-select";
import TaskPrioritySelect from "@cc/task/task-priority-select";
import DatePicker from "vue2-datepicker";
import { maxLength, minLength, required, requiredIf } from "vuelidate/lib/validators";

export default {
  components: { DatePicker, SweetTextarea, OrganizationMemberSelect, TaskPrioritySelect, TaskLinkSelect, FileRocket },
  data() {
    return {
      form: {},
      taskRelation: null,
      linkType: null,
      selectedFile: undefined,
      allowedFormats: ["application/*", "image/*"],
    };
  },
  validations: {
    form: {
      name: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(350),
      },
      description: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(4000),
      },
      doneReason: {
        required: requiredIf(function(form) {
          return form.status == "DONE";
        }),
        minLength: minLength(1),
        maxLength: maxLength(4000),
      },
    },
  },
  computed: {
    selectedFileIdInObject() {
      return this.selectedFile ? { selectedFile: this.selectedFile._id } : { selectedFile: undefined };
    },
    isUploading() {
      return this.$store.getters["fileManager/isUploading"];
    },
    organizationMembers() {
      return this.$store.getters["organizationMember/pagination"]?.docs ?? [];
    },
    language() {
      return this.$store.getters["user/account"]?.language ?? "tr";
    },
    linkButtonEnable() {
      if (!this.linkType || !this.taskRelation || this.form.taskRelations?.find((i) => i.task._id == this.taskRelation._id && this.linkType == i.type)) {
        return false;
      }
      return true;
    },
    targetUrl() {
      return `task/${this.form._id}/upload`;
    },
    task() {
      return this.$store.getters["task/task"];
    },
  },
  watch: {
    "$v.form": {
      deep: true,
      handler(v) {
        if (v.$anyError) {
          this.$emit("onFormError", this.$v);
        } else {
          this.$emit("onFormError", null);
        }
      },
    },
    task: {
      deep: true,
      immediate: true,
      handler(v) {
        this.form = {};
        if (v) {
          this.form = { ...this.prepareFormForEdit(v), duedate: new Date(v.duedate).getTime(), donedate: v.donedate ? new Date(v.donedate).getTime() : new Date().getTime() };
        } else {
          //güncel tarih + 30 gün koyup belirlemeli
          this.form = { duedate: new Date().getTime() + 30 * 24 * 60 * 60 * 1000 };
        }
      },
    },
    form: {
      deep: true,
      handler(v) {
        this.$v.$touch();
        this.$store.commit("task/form", { ...v });
      },
    },
  },
  mounted() {
    this.$v.$touch();
    if (this.organizationMembers.length == 0)
      this.$store.dispatch("organizationMember/paginateWith", { pagination: {}, organizationId: this.$store.getters["organization/selected"]?._id });
  },
  methods: {
    async attachFile() {
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch("task/attachFile", { fileId: this.selectedFile._id, taskId: this.task._id });
        this.$toast.success(this.$t("common.success"));
        this.reloadwith(this.task);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.selectedFile = undefined;
      }
    },
    handleUploadResponse(event) {
      if (!this.isUploading) {
        if (event && event?.status !== 200) {
          this.$handleHttpErrorByStatusCode(event?.error);
        }
        setTimeout(() => {
          this.reloadwith(this.task);
        }, 1000);
      }
    },
    selectFile(item) {
      this.selectedFile = item;
    },
    async onFileSearch(value, callback) {
      const categories = await this.$store.dispatch(`organizationFileCategory/paginateParentCategories`, {
        options: { limit: 200 },
        query: { categoryDefinition: { value: "TASK", isObject: true }, ...(this.scope ? { products: { value: this.scope, isObject: true } } : {}) },
      });

      const category = categories.docs?.[0];

      const response = await this.$store.dispatch(`fileManager/paginateSimple`, {
        options: { limit: 200 },
        query: {
          name: { value },
          ...(category ? { category: { value: { _id: category._id }, isObject: true } } : {}),
          ...(this.scope ? { products: { value: this.scope, isObject: true } } : {}),
        },
      });

      callback(response.data?.docs ?? []);
    },

    onMemberSelect(item) {
      this.form = { ...this.form, assignee: item };
    },
    onStatusSelect(item) {
      this.form = { ...this.form, status: item };
    },
    onPrioritySelect(item) {
      this.form = { ...this.form, priority: item };
    },

    async onCategorySearch(value, callback) {
      const response = await this.$store.dispatch(`taskCategory/paginate`, {
        options: { limit: 200 },
        query: { name: { value } },
      });

      callback(response.data?.docs ?? []);
    },
    onCategorySelect(category) {
      this.form = { ...this.form, category };
    },

    selectLinkTask(item) {
      this.taskRelation = item;
    },

    link() {
      if (!this.linkType || !this.taskRelation || this.form.taskRelations?.find((i) => i.task._id == this.taskRelation._id && this.linkType == i.type)) {
        return;
      }
      this.form.taskRelations = [...(this.form.taskRelations ?? []), { type: this.linkType, task: this.taskRelation }];

      this.linkType = null;
      this.taskRelation = null;
      this.$store.commit("task/form", { ...this.form });
    },

    unlink(item) {
      this.form.taskRelations = [...(this.form.taskRelations ?? []).filter((i) => !(i.type == item.type && i.task._id == item.task._id))];
      this.$forceUpdate();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../modules/task/tasks.scss";
</style>
